﻿import { useEffect, useMemo, useState, type FC } from 'react';
import { Flex } from 'antd';

import useUserInfo from 'hooks/useUserInfo';
import { ServiceAreaType, Suburb } from 'types';
import { useTechnicianServiceArea } from '../hooks/useTechnicianServiceArea';
import { useAssignableServiceAreaSuburbs } from '../hooks/useAssignableServiceAreaSuburbs';
import { TechnicianServiceAreaControls } from './technician-service-area-controls.component';
import { TechnicianServiceAreaTransfer, type TransferSuburbItem } from './technician-service-area-transfer.component';
import { TechnicianServiceAreaMap } from './technician-service-area-map.component';
import styles from './technician-service-area.module.scss';

type Props = Readonly<{
  technicianId?: string;
}>;

const DEFAULT_RADIUS_KM = 10;

const formatSuburbLabel = (suburb: Pick<Suburb, 'suburb' | 'state' | 'postcode'>): string =>
  `${suburb.suburb} ${suburb.state} ${suburb.postcode}`;

export const TechnicianServiceArea: FC<Props> = ({ technicianId }) => {
  const [areaType, setAreaType] = useState<ServiceAreaType>(ServiceAreaType.Primary);
  const [radius, setRadius] = useState<number>(DEFAULT_RADIUS_KM);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  const [user] = useUserInfo();
  const { suburbs, home, isServiceAreaLoading, saveServiceArea, isServiceAreaSaving } =
    useTechnicianServiceArea(technicianId);
  const {
    assignableSuburbs,
    isAssignableSuburbsLoading,
    searchAssignableSuburbs,
    settledSearchTechnicianId,
  } = useAssignableServiceAreaSuburbs(technicianId);

  const isCandidateSearchPending = Boolean(technicianId) && settledSearchTechnicianId !== technicianId;

  useEffect(() => {
    if (technicianId) {
      searchAssignableSuburbs(DEFAULT_RADIUS_KM);
    }
  }, [technicianId, searchAssignableSuburbs]);

  // The last search's candidates can go stale relative to a just-completed save: searching isn't re-triggered
  // automatically, so a suburb the CSS user just assigned would otherwise still show up as an Unallocated
  // candidate too, on both the Transfer and the map. Re-deriving here against the freshest `suburbs` keeps that
  // invariant (a suburb is never both assigned and a candidate) true regardless of how stale the search is.
  // Memoized so this keeps the same array reference across renders that don't touch suburbs/assignableSuburbs —
  // the map below re-fits itself whenever this array identity changes, so a fresh one on every unrelated
  // render (e.g. the radius input changing) would have it needlessly re-fitting.
  const effectiveAssignableSuburbs = useMemo(() => {
    const assignedSuburbIds = new Set((suburbs ?? []).map((suburb) => suburb.suburb_ID));
    return (assignableSuburbs ?? []).filter((suburb) => !assignedSuburbIds.has(suburb.ID));
  }, [suburbs, assignableSuburbs]);

  // Switching Area Type, running a new search, or refreshing after a save all reset the staged Unallocated
  // selection back to the current candidate pool — Save only ever applies to one Area Type at a time, so an
  // in-progress edit doesn't leak across tabs.
  useEffect(() => {
    setTargetKeys(effectiveAssignableSuburbs.map((suburb) => String(suburb.ID)));
  }, [areaType, assignableSuburbs, suburbs]);

  const selectedSuburbsForActiveType = (suburbs ?? []).filter((suburb) => suburb.primary_secondary === areaType);

  const dataSourceByKey = new Map<string, TransferSuburbItem & { suburbId: number }>();

  effectiveAssignableSuburbs.forEach((suburb) => {
    dataSourceByKey.set(String(suburb.ID), {
      key: String(suburb.ID),
      title: formatSuburbLabel(suburb),
      suburbId: suburb.ID,
    });
  });

  selectedSuburbsForActiveType.forEach((suburb) => {
    dataSourceByKey.set(String(suburb.suburb_ID), {
      key: String(suburb.suburb_ID),
      title: formatSuburbLabel(suburb),
      suburbId: suburb.suburb_ID,
    });
  });

  // Matches the Figma design's own suburb ordering (both panels list alphabetically).
  const dataSource = Array.from(dataSourceByKey.values()).sort((a, b) => a.title.localeCompare(b.title));

  const handleSave = () => {
    const selectedSuburbIds = dataSource.filter((item) => !targetKeys.includes(item.key)).map((item) => item.suburbId);

    saveServiceArea({ techId: Number(technicianId), area_type: areaType, suburb_ids: selectedSuburbIds });
  };

  return (
    <Flex gap={32} align="flex-start" className={styles.serviceArea}>
      <Flex vertical gap={16} className={styles.serviceArea__transferColumn}>
        <TechnicianServiceAreaControls
          areaType={areaType}
          onAreaTypeChange={setAreaType}
          radius={radius}
          onRadiusChange={setRadius}
          onSearch={() => searchAssignableSuburbs(radius)}
          isSearching={isAssignableSuburbsLoading || isCandidateSearchPending}
          isAdmin={!!user?.is_admin}
        />
        <TechnicianServiceAreaTransfer
          dataSource={dataSource}
          targetKeys={targetKeys}
          onChange={setTargetKeys}
          isLoading={isServiceAreaLoading}
        />
      </Flex>
      <TechnicianServiceAreaMap
        suburbs={suburbs ?? []}
        home={home ?? null}
        assignableSuburbs={effectiveAssignableSuburbs}
        onSave={handleSave}
        isSaving={isServiceAreaSaving}
        isSaveDisabled={isServiceAreaLoading}
        isCandidateSearchPending={isCandidateSearchPending}
      />
    </Flex>
  );
};
